import { ReactElement, useCallback, useId, useState } from 'react';
import ReactCollapsible from 'react-collapsible';

import { ChevronDown } from '@calm-web/icons';

import { FontSizes } from '../../utils/typography/types';
import Text from '../Text';
import { getDefaultSize } from '../Text/styles';
import { HeaderElement, HeaderElements } from '../Text/types';
import { CollapsibleInner, IconContainer, StyledTrigger } from './styles';
import { CollapsibleProps } from './types';

const CollapsibleTrigger = ({
	trigger,
	isOpen,
	isDisabled,
	triggerTextColor,
	iconSize,
	iconColor,
	animationTimeMs,
}: Pick<CollapsibleProps, 'trigger' | 'isDisabled'> &
	Required<Pick<CollapsibleProps, 'isOpen' | 'triggerTextColor' | 'iconSize' | 'iconColor'>> & {
		animationTimeMs: number;
	}): ReactElement => {
	const content = (() => {
		if (typeof trigger === 'string') {
			return (
				<Text size={FontSizes.base} color={triggerTextColor}>
					{trigger}
				</Text>
			);
		}
		return trigger;
	})();
	return (
		<StyledTrigger $isDisabled={isDisabled}>
			{isDisabled ? null : (
				<IconContainer
					$size={iconSize}
					$color={iconColor}
					$isOpen={isOpen}
					$animationTimeMs={animationTimeMs}
				>
					<ChevronDown />
				</IconContainer>
			)}
			{content}
		</StyledTrigger>
	);
};

const Collapsible = ({
	className,
	trigger,
	triggerTextColor = 'collapsibleText',
	iconSize = FontSizes.base,
	iconColor = 'collapsibleIcon',
	isOpen: _isOpen,
	defaultOpen,
	isDisabled,
	onClick: _onClick,
	onOpened,
	onClosed,
	children,
	tabIndex = 0,
}: CollapsibleProps): ReactElement => {
	const [localIsOpen, setLocalIsOpen] = useState(defaultOpen ?? false);
	const isOpen = _isOpen ?? localIsOpen;
	const animationTimeMs = 200;

	const onClick = useCallback(() => {
		setLocalIsOpen(!localIsOpen);
		if (_onClick) {
			_onClick();
		}
	}, [localIsOpen, _onClick]);

	const rawId = useId().replace(/[^a-zA-Z0-9-_]/g, '');
	const baseId = rawId.match(/^[0-9]/) ? `c${rawId}` : rawId;
	const triggerId = `${baseId}-trigger`;
	const contentId = `${baseId}-content`;

	return (
		<ReactCollapsible
			trigger={
				<CollapsibleTrigger
					trigger={trigger}
					triggerTextColor={triggerTextColor}
					isOpen={isOpen}
					iconSize={iconSize}
					iconColor={iconColor}
					animationTimeMs={animationTimeMs}
					isDisabled={isDisabled}
				/>
			}
			triggerElementProps={{ id: triggerId }}
			className={className}
			openedClassName={className}
			open={isOpen}
			triggerDisabled={isDisabled}
			handleTriggerClick={onClick}
			onOpen={onOpened}
			onClose={onClosed}
			transitionTime={animationTimeMs}
			easing="ease-in-out"
			tabIndex={tabIndex}
			contentElementId={contentId}
		>
			<CollapsibleInner $isOpen={isOpen}>{children}</CollapsibleInner>
		</ReactCollapsible>
	);
};

export const CollapsibleSection = ({
	headerText,
	headerEl = HeaderElements.H3,
	...props
}: CollapsibleProps & {
	headerText: string;
	headerEl?: HeaderElement;
	trigger: never;
}): ReactElement => {
	const trigger = <Text el={headerEl}>{headerText}</Text>;
	const iconSize = getDefaultSize(headerEl);
	return <Collapsible iconSize={iconSize} {...props} trigger={trigger} />;
};

export default Collapsible;
